import Decimal from "decimal.js-light";

export interface PercentAllocation<T> {
  id: T;
  percent: number;
}

export interface NormalizePercentOptions {
  target?: number;
  places?: number;
}

export interface NormalizePercentResult<T> {
  normalized: Array<{ id: T; percent: number }>;
  sum: number;
}

const ONE_HUNDRED = new Decimal(100);

export function normalizePercentAllocations<T>(
  allocations: PercentAllocation<T>[],
  options: NormalizePercentOptions = {}
): NormalizePercentResult<T> {
  const { target, places = 2 } = options;

  if (!Array.isArray(allocations) || allocations.length === 0) {
    const resolvedTarget = typeof target === "number" ? target : 0;
    return { normalized: [], sum: resolvedTarget };
  }

  const decimals = allocations.map((item) => ({
    id: item.id,
    percentDec: new Decimal(item.percent ?? 0),
  }));

  const originalSum = decimals.reduce((acc, item) => acc.plus(item.percentDec), new Decimal(0));
  const expected =
    typeof target === "number" ? new Decimal(target) : originalSum.isZero() ? new Decimal(0) : ONE_HUNDRED;

  const rounded = decimals.map((item) => ({
    id: item.id,
    percentDec: item.percentDec.toDecimalPlaces(places, Decimal.ROUND_HALF_UP),
  }));

  const roundedSum = rounded.reduce((acc, item) => acc.plus(item.percentDec), new Decimal(0));
  const diff = expected.minus(roundedSum);
  const DIFF_THRESHOLD = new Decimal("0.01");

  if (!diff.isZero() && diff.abs().lte(DIFF_THRESHOLD)) {
    const lastIdx = rounded.length - 1;
    rounded[lastIdx] = {
      ...rounded[lastIdx],
      percentDec: rounded[lastIdx].percentDec.plus(diff),
    };
  }

  const normalized = rounded.map((item) => ({
    id: item.id,
    percent: item.percentDec.toNumber(),
  }));

  const finalSum = normalized.reduce((acc, item) => acc.plus(item.percent), new Decimal(0));

  return {
    normalized,
    sum: finalSum.toNumber(),
  };
}
